// Contour color mapping along a piping path. Inputs:
//   • a sequence of arc-length samples (s in mm)
//   • a sorted list of CML positions with values (s + numeric value or status)
//
// For each sample we find the bracketing pair of CMLs and blend their colors
// linearly in HCL-ish RGB. Outside the bracketing range we extend the nearest
// CML color (clamped, not extrapolated). Samples on a pipe that has *no*
// nearby CML within `maxReachMm` fall back to neutral ink.

import { Color } from "three";
import type { CML, CMLStatus } from "./types";

export type ColorMode = "status" | "cr" | "rl";

/** Discrete status colors (used in 2D legend). */
export const STATUS_COLORS: Record<CMLStatus, string> = {
  ok: "#1E6F4D",
  watch: "#B45309",
  warn: "#C2410C",
  crit: "#9F1239",
  retired: "#6E7585",
};

// Cool paper-tone gray — fades pipe surface toward the cream backdrop where
// no CML data is available, while staying readable as a pipe (not invisible).
const NEUTRAL = "#A3ADB8";

/** Continuous palette (low→high concern). Pure-data style, like thermal. */
const PALETTE_HEAT = [
  "#0E4675", // deep blue (good)
  "#3E7CB1", // soft blue
  "#7CAE6E", // green
  "#D8B656", // amber
  "#C24F2F", // orange
  "#8A1E2E", // crimson (bad)
];

export interface CmlSample {
  cmlId: string;
  s: number; // arc length mm along path
  value: number; // already mapped to 0..1
  status: CMLStatus;
}

/**
 * Map a CML to a 0..1 value for a chosen color mode.
 *   STATUS: ok=0.0, watch=0.33, warn=0.66, crit=1.0, retired=0.5
 *   CR    : clamp(crMmYr / 0.6, 0, 1) — 0.6 mm/yr is "very high"
 *   RL    : 1 - clamp(rlYr / 30, 0, 1) — invert so short RL = high concern
 */
export function cmlValue(cml: CML, mode: ColorMode): number {
  if (mode === "status") {
    const map: Record<CMLStatus, number> = {
      ok: 0.05,
      watch: 0.35,
      warn: 0.66,
      crit: 0.96,
      retired: 0.5,
    };
    return map[cml.calc.status];
  }
  if (mode === "cr") {
    return Math.max(0, Math.min(1, cml.calc.effectiveCrMmYr / 0.6));
  }
  // rl: short remaining life = high concern
  return 1 - Math.max(0, Math.min(1, cml.calc.remainingLifeYr / 30));
}

/** Sample a value t∈[0,1] from the continuous palette. */
export function paletteColor(t: number, target = new Color()): Color {
  const clamped = Math.max(0, Math.min(1, t));
  const n = PALETTE_HEAT.length - 1;
  const idx = clamped * n;
  const i = Math.floor(idx);
  const f = idx - i;
  const a = new Color(PALETTE_HEAT[i]);
  const b = new Color(PALETTE_HEAT[Math.min(n, i + 1)]);
  target.copy(a).lerp(b, f);
  return target;
}

/** Same palette, but as hex string (used in legends/UI). */
export function paletteHex(t: number): string {
  return "#" + paletteColor(t).getHexString();
}

/**
 * Build an array of THREE.Color values, one per arc-length sample, by
 * interpolating along the path between CMLs.
 *
 * - Samples beyond all CMLs adopt the nearest CML color (no extrapolation).
 * - Samples > `falloffMm` from any CML fade toward NEUTRAL.
 */
export function buildContourColors(
  sampleS: number[],
  cmls: CmlSample[],
  falloffMm = 2200,
): Color[] {
  const neutral = new Color(NEUTRAL);
  if (cmls.length === 0) return sampleS.map(() => neutral.clone());

  // Sort CMLs by arc length
  const pts = [...cmls].sort((a, b) => a.s - b.s);

  return sampleS.map((s) => {
    // Find bracketing CMLs
    let lo: CmlSample | null = null;
    let hi: CmlSample | null = null;
    for (const c of pts) {
      if (c.s <= s) lo = c;
      if (c.s >= s && hi === null) hi = c;
    }
    // Outside left
    if (!lo && hi) {
      const dist = hi.s - s;
      return blendNeutral(paletteColor(hi.value), neutral, dist, falloffMm);
    }
    // Outside right
    if (lo && !hi) {
      const dist = s - lo.s;
      return blendNeutral(paletteColor(lo.value), neutral, dist, falloffMm);
    }
    // Both inside — interpolate
    if (lo && hi) {
      if (lo === hi) return paletteColor(lo.value).clone();
      const segLen = hi.s - lo.s;
      const t = segLen > 0 ? (s - lo.s) / segLen : 0;
      const v = lo.value + (hi.value - lo.value) * t;
      // gentle dim between CMLs if both far away
      return paletteColor(v).clone();
    }
    return neutral.clone();
  });
}

function blendNeutral(base: Color, neutral: Color, distMm: number, falloff: number): Color {
  const fade = Math.min(1, distMm / falloff);
  return base.clone().lerp(neutral, fade * 0.55);
}
